import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CHANNEL_SHORT_NAME_MAP,
  CHANNEL_STATUS,
  IChannel,
  PROTOCOL,
  TChannelStatus,
} from '@/entities/channel';
import { Box } from '@/shared/ui';
import { Dialog, TDialogProps } from '@/shared/ui/Dialog';
import { Button } from '@/shared/ui/lib/Button';
import { Loader } from '@/shared/ui/Loader';

import { useChannelByIdImperative } from '../../model/useChannelByIdImperative';
import { ChannelIconWithLabel } from '../ChannelIconWithLabel';

import { ENTER_PASSWORD_FORM_ID } from './EnterPasswordTgPersonalForm';
import { TelegramAuthProvider, useTelegramAuth } from './TelegramAuthContext';
import { TELEGRAM_PERSONAL_EMAIL_FORM_ID, TelegramPersonalEmail } from './TelegramPersonalEmail';
import { TelegramPersonalCloudPassword } from './TelegramPersonalCloudPassword';
import {
  TELEGRAM_PERSONAL_PHONE_FORM_ID,
  TelegramPersonalPhoneForm,
} from './TelegramPersonalPhoneForm';
import { TelegramPersonalQrCode } from './TelegramPersonalQrCode';
import { TelegramPersonalSelectAuthMethod } from './TelegramPersonalSelectAuthMethod';
import {
  TELEGRAM_PERSONAL_VERIFY_CODE_FORM_ID,
  TelegramPersonalSMS,
} from './TelegramPersonalSMS';

export interface ITelegramPersonalAuthDialogProps extends TDialogProps {
  open: boolean;
  channel: IChannel;
  isPending?: boolean;
  onAuthorize?: () => void;
}

const STATUS_POLL_ATTEMPTS = 20;
const STATUS_POLL_INTERVAL_MS = 1000;

const wait = (ms: number) => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

const isPasswordRequired = (status?: TChannelStatus) => {
  return status === CHANNEL_STATUS.PASSWORD_REQUIRED;
};

const isAuthorizedStatus = (status?: TChannelStatus) => {
  return (
    status === CHANNEL_STATUS.AUTHORIZED ||
    status === CHANNEL_STATUS.READY ||
    status === CHANNEL_STATUS.CONTACTS_SYNCED ||
    status === CHANNEL_STATUS.CHATS_SYNCED ||
    status === CHANNEL_STATUS.ACTIVE
  );
};

const TelegramPersonalAuthDialogInProvider = ({
  open,
  onClose,
  channel,
  title,
  isPending,
  onAuthorize,
  ...otherProps
}: ITelegramPersonalAuthDialogProps) => {
  const { t } = useTranslation();
  const { isLoading, step, authResult, setStep, setIsLoading } = useTelegramAuth();
  const [isCodeValid, setIsCodeValid] = useState<boolean>(false);
  const [phone, setPhone] = useState<string>(channel.phone_number ?? '');
  const { fetchChannelById } = useChannelByIdImperative();

  useEffect(() => {
    if (step === 'ready') onAuthorize?.();
  }, [step, onAuthorize]);

  const handlerSelectQr = () => {
    setStep('qr');
  };

  const handlerSelectPhone = () => {
    setStep('phone');
  };

  const handleBack = () => {
    if (step === 'sms') {
      setStep(authResult?.step === 'email_setup_code' ? 'email' : 'phone');
    } else {
      onClose?.();
    }
  };

  const getTitle = () => {
    if (step === 'selectMethod') return t('channels:authSelectAuthMethod');
    if (step === 'qr') return t('channels:authByQrCodeTitle');
    if (step === 'phone' || step === 'email' || step === 'sms')
      return t('channels:authByPhoneTitle');
    if (step === 'password') return t('channels:tgPersonal.enterPasswordTitle');

    if (title) return title;
    return t('channels:dialogTitle');
  };

  const handlePasswordStep = async () => {
    setIsLoading(true);
    try {
      for (let attempt = 0; attempt < STATUS_POLL_ATTEMPTS; attempt += 1) {
        const data = await fetchChannelById(channel.id);
        const nextStatus = data?.status ?? channel.status;

        if (isPasswordRequired(nextStatus)) {
          setStep('password');
          return;
        }

        if (isAuthorizedStatus(nextStatus)) {
          onAuthorize?.();
          return;
        }

        await wait(STATUS_POLL_INTERVAL_MS);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 'selectMethod':
        return (
          <TelegramPersonalSelectAuthMethod
            onSelectQr={handlerSelectQr}
            onSelectPhone={handlerSelectPhone}
          />
        );
      case 'qr':
        return (
          <TelegramPersonalQrCode
            channel={channel}
            onAuthorize={handlePasswordStep}
            enabledQuery={open}
          />
        );
      case 'phone':
        return (
          <TelegramPersonalPhoneForm
            setPhone={setPhone}
            channelId={channel.id}
          />
        );
      case 'email':
        return <TelegramPersonalEmail channelId={channel.id} />;
      case 'sms':
        return (
          <TelegramPersonalSMS
            phone={phone}
            channelId={channel.id}
            setIsCodeValid={setIsCodeValid}
          />
        );
      case 'password':
        return (
          <TelegramPersonalCloudPassword
            channelId={channel.id}
            onSuccess={onAuthorize}
          />
        );
      default:
        return null;
    }
  };

  const getFormIdByStep = () => {
    if (step === 'phone') return TELEGRAM_PERSONAL_PHONE_FORM_ID;
    if (step === 'email') return TELEGRAM_PERSONAL_EMAIL_FORM_ID;
    if (step === 'sms') return TELEGRAM_PERSONAL_VERIFY_CODE_FORM_ID;
    if (step === 'password') return ENTER_PASSWORD_FORM_ID;
    return undefined;
  };

  const getRightButtonText = () => {
    if (step === 'phone' || step === 'email') return t('channels:buttons.sendCode');
    if (step === 'sms') return t('common:buttons.continue');
    if (step === 'password')
      return t('channels:tgPersonal.confirmPassword.confirmBtn');
    return '';
  };

  const getRightButtonDisabled = () => {
    if (step === 'sms') return !isCodeValid;
    return false;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={getTitle()}
      leftButtonSlot={
        <Button variant="outlined" onClick={handleBack}>
          {step === 'sms'
            ? t('channels:buttons.moveBack')
            : t('channels:buttons.cancel')}
        </Button>
      }
      rightButtonSlot={
        (step === 'phone' || step === 'email' || step === 'sms' || step === 'password') && (
          <Button
            variant="contained"
            type="submit"
            form={getFormIdByStep()}
            color="primary"
            loading={isLoading}
            disabled={getRightButtonDisabled()}
          >
            {getRightButtonText()}
          </Button>
        )
      }
      additionalTitleSlot={
        <ChannelIconWithLabel
          label={CHANNEL_SHORT_NAME_MAP[PROTOCOL.TELEGRAM_PERSONAL]}
          protocol={PROTOCOL.TELEGRAM_PERSONAL}
        />
      }
      {...otherProps}
    >
      <Box sx={{ maxWidth: '670px', width: '100%' }}>
        {isPending ? <Loader containerSize="small" /> : renderStep()}
      </Box>
    </Dialog>
  );
};

export const TelegramPersonalAuthDialog = ({
  channel,
  ...props
}: ITelegramPersonalAuthDialogProps) => {
  return (
    <TelegramAuthProvider
      key={`${channel.id}-${props.open}`}
      initialStep="selectMethod"
    >
      <TelegramPersonalAuthDialogInProvider channel={channel} {...props} />
    </TelegramAuthProvider>
  );
};
